import React, { useEffect, useState } from 'react';
import { normalizeOfferFilter, offerFilterChange, parseOfferPrice } from '../../util/offerSearch';
import css from './OfferSearchFilters.module.css';

export const OfferTypeFilter = ({ value, onChange, compact = false }) => (
  <fieldset className={`${css.mode} ${compact ? css.compact : ''}`}>
    <legend className={css.label}>Type d’offre</legend>
    <div className={css.options}>
      {[
        ['all', 'Tous'],
        ['rent', 'À louer'],
        ['sale', 'À vendre'],
      ].map(([key, label]) => (
        <button
          type="button"
          key={key}
          className={normalizeOfferFilter(value) === key ? css.selected : css.option}
          aria-pressed={normalizeOfferFilter(value) === key}
          onClick={() => {
            if (normalizeOfferFilter(value) !== key) onChange(offerFilterChange(key));
          }}
        >
          {label}
        </button>
      ))}
    </div>
  </fieldset>
);

export const OfferPriceFilter = ({
  offerType,
  value,
  onChange,
  currency = 'EUR',
  compact = false,
  budgetUnit,
  requestedSeats,
  allowRentBudgetUnit = false,
}) => {
  const range = parseOfferPrice(value);
  const [min, setMin] = useState(range?.min ?? '');
  const [max, setMax] = useState(range?.max ?? '');
  const [error, setError] = useState(false);
  const [rentUnit, setRentUnit] = useState(budgetUnit === 'seat' ? 'seat' : 'total');
  useEffect(() => {
    const next = parseOfferPrice(value);
    setMin(next?.min ?? '');
    setMax(next?.max ?? '');
    setError(false);
    setRentUnit(budgetUnit === 'seat' ? 'seat' : 'total');
  }, [value, offerType, budgetUnit]);
  const mode = normalizeOfferFilter(offerType);
  const label = mode === 'sale' ? 'Prix de vente' : mode === 'rent' ? 'Loyer mensuel' : 'Budget';
  const unit = currency === 'EUR' ? '€' : currency;
  const applyButton = (
    <button type="submit" className={css.apply}>
      Appliquer
    </button>
  );
  const clearButton = (value || compact) && (
    <button
      type="button"
      className={css.clear}
      onClick={() => onChange({ price: null, page: null })}
    >
      Effacer le budget
    </button>
  );
  return (
    <form
      className={`${css.budget} ${compact ? css.compactBudget : ''}`}
      onSubmit={event => {
        event.preventDefault();
        const price = `${min},${max}`;
        if (price !== ',' && !parseOfferPrice(price)) {
          setError(true);
          return;
        }
        setError(false);
        onChange({
          price: price === ',' ? null : price,
          page: null,
          ...(allowRentBudgetUnit && mode === 'rent' ? { budgetUnit: rentUnit } : {}),
        });
      }}
    >
      <fieldset className={css.range}>
        <legend className={css.label}>
          {label} ({unit})
        </legend>
        {allowRentBudgetUnit && mode === 'rent' && (
          <label className={css.unitLabel}>
            Calcul du loyer
            <select
              aria-label="Calcul du loyer"
              value={rentUnit}
              onChange={event => {
                setRentUnit(event.target.value);
                setMin('');
                setMax('');
                setError(false);
              }}
            >
              <option value="total">Par mois, en tout</option>
              <option value="seat">Par poste et par mois</option>
            </select>
          </label>
        )}
        <div className={css.inputs}>
          <label className={css.inputLabel}>
            <span>Minimum</span>
            <input
              aria-label={`${label} minimum`}
              type="number"
              inputMode="decimal"
              min="0"
              max="1000000000"
              step="0.01"
              placeholder="Sans min."
              value={min}
              onChange={e => setMin(e.target.value)}
            />
          </label>
          <label className={css.inputLabel}>
            <span>Maximum</span>
            <input
              aria-label={`${label} maximum`}
              type="number"
              inputMode="decimal"
              min="0"
              max="1000000000"
              step="0.01"
              placeholder="Sans max."
              value={max}
              onChange={e => setMax(e.target.value)}
            />
          </label>
          {!compact && applyButton}
        </div>
      </fieldset>
      {error && (
        <p className={css.error} role="alert">
          Le maximum doit être supérieur ou égal au minimum.
        </p>
      )}
      {mode === 'all' && (
        <p className={css.hint}>
          Choisissez « À louer » ou « À vendre » pour préciser votre budget.
        </p>
      )}
      {allowRentBudgetUnit && mode === 'rent' && (
        <p className={css.hint}>
          {rentUnit === 'seat'
            ? 'Les loyers globaux sont rapportés au nombre de postes de l’annonce.'
            : `Les tarifs au poste sont calculés pour ${
                Number(requestedSeats) > 0
                  ? `${Number(requestedSeats)} postes`
                  : 'tous les postes de l’annonce'
              }.`}
        </p>
      )}
      {compact ? (
        <div className={css.actions}>
          {clearButton}
          {applyButton}
        </div>
      ) : (
        clearButton
      )}
    </form>
  );
};
